import {
  showNewPaymentMethodForm,
  savePaymentMethodFieldsFailure,
  hideNewPaymentMethodForm
} from 'reduxModules/forms/paymentMethod'
import {
  activateSubscribingMode,
  deactivateSubscribingMode,
  validateAddress,
  lifetimePreviewClear,
  types as checkoutTypes
} from 'reduxModules/checkout'
import { clearDiscountCode } from 'reduxModules/discountCode'
import { initConfirmation } from 'reduxModules/confirmation'
import {
  seedLifetimeFromPlanList,
  fetchLifetimePreview
} from 'reduxModules/checkout/dispatchers'
import { planSelectedSelector } from 'reduxModules/checkout/selectors'
import _ from 'lodash'
import modalDispatchers from 'reduxModules/generic/modal/dispatchers'
import {
  subscriptionPlansFetch,
  subscriptionsFetch
} from 'reduxModules/subscriptions'
import { clearRecaptchaToken, validateUpgradeCoupon } from 'reduxModules/app'
import { navigate } from '../../../utils/navigation'
import {
  getUserProfile,
  getUserTutorials,
  getUserFavorites,
  updateUserProfile,
  createUserAddress,
  updateUserAddress,
  getUserAddresses,
  removeUserAddress,
  getUserPaymentMethods,
  markDefaultUserPaymentMethod,
  removePaymentMethod,
  getUserBraintreeToken,
  savePaymentMethod,
  savePaymentMethodFailure,
  editingAddress,
  types as userTypes,
  getUserOrders,
  getOrderDetail,
  showAddressFormAction,
  hideAddressFormAction,
  changePlan,
  getUserLearningPaths,
  closeProfile,
  closeExternalUserFunnelDialog
} from './'
import notifications, {
  toastError,
  toastFailure,
  toastServerError,
  toastSuccess
} from '../../../global/notifications'

import fixturePlansLegacy from '../../../components/SubscriptionPlans/fixturePlansLegacy'

// Braintree SDK messages are written for developers ("Cannot tokenize empty
// card fields"); map the codes users can actually hit to our own copy and
// keep the SDK text only as a last resort.
const BRAINTREE_ERROR_COPY = {
  HOSTED_FIELDS_FIELDS_EMPTY: 'Please enter your card details to continue.',
  HOSTED_FIELDS_FAILED_TOKENIZATION:
    "We couldn't process this card. Please check the details or try a different card.",
  HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE:
    'This card is already saved to your account. Please select it from your saved payment methods.',
  HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR:
    "We couldn't reach the payment service. Please check your connection and try again.",
  HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED:
    "The security code (CVV) doesn't match this card. Please double-check it or try a different card."
}

export const braintreeErrorHandler = (dispatch, error) => {
  if (error.code === 'HOSTED_FIELDS_FIELDS_INVALID')
    dispatch(savePaymentMethodFieldsFailure(error))
  else {
    dispatch(savePaymentMethodFailure(error))
    toastServerError(BRAINTREE_ERROR_COPY[error.code] || error.message)
  }
}

export const profileDispatchers = (dispatch) => ({
  loadUserProfile() {
    dispatch(getUserProfile())
  },
  onSubmit(data) {
    data = { ...data }
    Object.keys(data).forEach((key) => {
      if (key.startsWith('movie')) {
        data[`movie_${key.substr(5)}`] = data[key] !== '' ? data[key] : null
        delete data[key]
      }
    })
    dispatch(updateUserProfile(data))
      .then(([_, res]) => {
        if (res.type === userTypes.USERS_PROFILE_UPDATE_SUCCESS) {
          toastSuccess(notifications.profileUpdate)
          dispatch(closeProfile())
        } else toastFailure(notifications.profileUpdate, res.payload)
      })
      .catch((error) => toastServerError(error.errors))
  },
  updateInterests(data) {
    dispatch(updateUserProfile(data))
      .then(([_, res]) => {
        if (res.type === userTypes.USERS_PROFILE_UPDATE_SUCCESS)
          toastSuccess(notifications.interestsUpdate)
        else toastFailure(notifications.interestsUpdate, res.payload)
      })
      .catch((error) => toastServerError(error.errors))
  }
})

export const tutorialsDispatchers = (dispatch) => ({
  loadUserTutorials() {
    dispatch(getUserTutorials())
  }
})

export const favoritesDispatchers = (dispatch) => ({
  loadFavorites() {
    dispatch(getUserFavorites())
  }
})

export const addressDispatchers = (dispatch) => ({
  onSubmit(data, dispatch, props) {
    import('reduxModules/utils/getCountryCode').then((getCountryCode) => {
      const countryCodeAlpha2 = getCountryCode.default(data.countryName)
      const { editing, values } = props
      const newData = {
        ..._.omitBy(data, _.isNil),
        countryCodeAlpha2,
        dismissWeightValidation: true
      }
      dispatch(validateAddress({ ...newData, id: undefined }))
        .then(([_, res]) => {
          if (res.type === checkoutTypes.VALIDATE_ADDRESS_SUCCESS) {
            const action = editing
              ? updateUserAddress(values.id)(newData)
              : createUserAddress(newData)

            dispatch(action).then(([_, res]) => {
              if (res.type === userTypes.USERS_ADDRESS_CREATE_SUCCESS)
                toastSuccess(notifications.addressCreated)
              else if (res.type === userTypes.USERS_ADDRESS_UPDATE_SUCCESS)
                toastSuccess(notifications.addressUpdated)
              else if (
                res.type === userTypes.USERS_ADDRESS_CREATE_REQUEST_FAILURE
              )
                toastError(notifications.addressCreated)
              else if (
                res.type === userTypes.USERS_ADDRESS_UPDATE_REQUEST_FAILURE
              )
                toastError(notifications.addressUpdated)
              dispatch(hideAddressFormAction())
            })
          }
        })
        .catch((payload) => {
          toastServerError(payload.errors)
        })
    })
  },
  showAddressForm: () => dispatch(showAddressFormAction()),
  hideAddressForm: () => dispatch(hideAddressFormAction())
})

export const addressesDispatchers = (dispatch) => ({
  loadAddresses() {
    dispatch(getUserAddresses())
  },
  removeAddress(pk, activeAddressFormId) {
    const confirmation = {
      title: 'Warning',
      text: 'Are you sure you want to remove this address?',
      options: [
        { resolveValue: 'remove', text: 'Remove', class: 'confirm-btn' }
      ]
    }
    dispatch(initConfirmation(confirmation)).on('remove', () =>
      dispatch(removeUserAddress(pk)).then(([_, res]) => {
        if (res.type === userTypes.USERS_ADDRESS_REMOVE_SUCCESS) {
          toastSuccess(notifications.addressRemoved)
          if (pk === activeAddressFormId) dispatch(hideAddressFormAction())
        } else toastFailure(notifications.addressRemoved, res.payload)
      })
    )
  },
  editAddress(address) {
    dispatch(editingAddress(address))
  },
  ...modalDispatchers(dispatch)
})

export const dispatchGetUserBraintreeToken = (dispatch) =>
  dispatch(getUserBraintreeToken())

export const paymentMethodListDispatchers = (dispatch) => {
  const markPaymentDefault = (token) =>
    dispatch(markDefaultUserPaymentMethod(token)())
      .then(([_, res]) => {
        if (res.type === userTypes.USERS_MARK_PAYMENT_METHOD_DEFAULT_SUCCESS) {
          toastSuccess(notifications.paymentMethodSetAsDefault)
          // SPEC-039 FR-2: the Primary tag renders from the Redux list, which
          // nothing refreshed — the promoted/demoted flags were stale until a
          // full page reload. Re-fetch so the tag moves from server truth.
          dispatch(getUserPaymentMethods())
        } else
          toastFailure(notifications.paymentMethodSetAsDefault, res.payload)
      })
      // fetchHelper resolves API failures into the tuple above (network
      // errors included); this guards anything unexpected so the flow can
      // never dead-end without user feedback again (SPEC-039 FR-3).
      .catch(() => toastError(notifications.paymentMethodSetAsDefault))

  return {
    loadPaymentMethods() {
      dispatch(getUserPaymentMethods())
    },
    markPaymentDefault,
    markPaymentDefaultWithconfirmation(token) {
      const confirmation = {
        title: 'Warning',
        text: 'Your subscription will now be charged to this card',
        options: [
          { resolveValue: 'accept', text: 'Accept', class: 'confirm-btn' }
        ]
      }
      // Closure, NOT `this.markPaymentDefault`: the component destructures
      // these handlers from props and calls them unbound, so `this` was
      // undefined and Accept crashed before any API call — the actual
      // silent dead-end in the SPEC-039 screencast.
      dispatch(initConfirmation(confirmation)).on('accept', () =>
        markPaymentDefault(token)
      )
    },
    removePaymentMethodDispatcher(token) {
      const confirmation = {
        title: 'Warning',
        text: 'Are you sure you want to remove this payment method?',
        options: [
          { resolveValue: 'remove', text: 'Remove', class: 'confirm-btn' }
        ]
      }
      dispatch(initConfirmation(confirmation)).on('remove', () =>
        dispatch(removePaymentMethod(token)).then(([_, res]) => {
          if (res.type === userTypes.USERS_PAYMENT_METHOD_REMOVE_SUCCESS)
            toastSuccess(notifications.paymentMethodDeleted)
          else toastFailure(notifications.paymentMethodDeleted, res.payload)
        })
      )
    },
    ...modalDispatchers(dispatch)
  }
}

export const dispatchSavePaymentMethod = (dispatch, payload, recaptcha) => {
  dispatch(savePaymentMethod(recaptcha)(payload))
    .then(([_, res]) => {
      if (res.type === userTypes.USERS_PAYMENT_METHOD_SAVE_SUCCESS) {
        toastSuccess(notifications.paymentMethodCreated)
        dispatch(hideNewPaymentMethodForm())
      } else
        // Surface the API's reason (declined card, CVV mismatch...) with the
        // canned copy only as fallback (form-error audit §2)
        toastFailure(notifications.paymentMethodCreated, res.payload)
    })
    .catch((error) => toastServerError(error.errors))
}

export const paymentMethodsDispatchers = (
  stateProps,
  dispatchProps,
  ownProps
) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    loadPaymentMethods() {
      dispatch(getUserPaymentMethods())
    },
    showNewPaymentMethodForm: () => dispatch(showNewPaymentMethodForm()),
    getBraintreeClientToken: () => {
      const { clientToken } = stateProps
      if (clientToken === '') dispatchGetUserBraintreeToken(dispatch)
    },
    savePaymentMethod(deviceData, tokenize, recaptcha) {
      tokenize()
        .then((payload) => {
          dispatchSavePaymentMethod(
            dispatch,
            {
              deviceData,
              paymentMethodNonce: payload.nonce
            },
            recaptcha
          )
        })
        .catch((error) => braintreeErrorHandler(dispatch, error))
        .finally(() => dispatch(clearRecaptchaToken()))
    },
    hideNewPaymentMethodForm: () => dispatch(hideNewPaymentMethodForm()),
    showFraudProtectionError() {
      toastError(notifications.fraudProtection)
    }
  }
}

export const ordersDispatchers = (dispatch) => ({
  loadOrders() {
    dispatch(getUserOrders())
  },
  loadSubscriptions() {
    dispatch(subscriptionsFetch())
  }
})

export const orderDetailDispatchers = (dispatch) => ({
  loadOrderDetail: (pk) => dispatch(getOrderDetail(pk)())
})

export const changeSubscriptionDispatchers = (dispatch) => ({
  dispatchUpgradePlan: (plans, newPlan, oldPlan, preAppliedCoupon) => {
    const isProrated = !(
      _.size(
        _.filter(fixturePlansLegacy, (p) => p.firstMonthPrice !== null)
      ) === 0
    )

    // Prefer the plans array passed in from the page (which carries the
    // user's *effective* current price) and only fall back to the static
    // fixturePlansLegacy for ranks not present there (e.g. Basic).
    const planInfo =
      _.find(plans, { rank: oldPlan }) ||
      _.find(fixturePlansLegacy, { rank: oldPlan })
    if (!planInfo) {
      toastError(notifications.unupgradeableSubscription)
      return
    }

    const isLifetime = newPlan.name === 'Lifetime'

    if (isLifetime) {
      const confirmation = {
        title: 'Upgrade subscription plan',
        discountCode: false,
        options: [
          { resolveValue: 'accept', text: 'continue', class: 'confirm-btn' }
        ],
        height: 800,
        newPlan,
        planInfo,
        isProrated: false,
        preAppliedCoupon: null,
        cancelCallback: () => {
          dispatch(deactivateSubscribingMode())
          dispatch(lifetimePreviewClear())
        },
        componentId: 'upgrade'
      }
      dispatch(initConfirmation(confirmation)).on('accept', () => {
        // Preview was fetched while dialog was open; /checkout will read it
        // from state.checkout.lifetimePreview and short-circuit a re-fetch
        // when planSelected.id matches.
        navigate('/checkout')
      })
      // Resolve real backend plan id, then fetch the upgrade preview so the
      // dialog can display upgrade credit + final charge inline.
      dispatch(async (d, getState) => {
        const seeded = await d(seedLifetimeFromPlanList())
        if (!seeded) return
        const planSelected = planSelectedSelector(getState())
        if (planSelected && planSelected.id)
          await d(fetchLifetimePreview(planSelected.id))
      })
      return
    }

    const confirmation = {
      title: 'Upgrade subscription plan',
      discountCode: !preAppliedCoupon,
      options: [
        { resolveValue: 'accept', text: 'upgrade', class: 'confirm-btn' }
      ],
      height: 820,
      newPlan,
      planInfo,
      isProrated,
      preAppliedCoupon,
      cancelCallback: () => {
        dispatch(deactivateSubscribingMode())
        dispatch(clearDiscountCode())
      },
      componentId: 'upgrade'
    }
    dispatch(activateSubscribingMode(newPlan))
    dispatch(initConfirmation(confirmation)).on('accept', (coupon) => {
      const request = { newPlan: newPlan.id }
      if (coupon) request.coupon = coupon.codename
      return dispatch(changePlan(request))
        .then(([_, res]) => {
          if (res.type === userTypes.USERS_CHANGE_PLAN_SUCCESS) {
            toastSuccess(notifications.upgradeSubscription)
            dispatch(subscriptionPlansFetch())
            dispatch(clearDiscountCode())
          } else toastFailure(notifications.upgradeSubscription, res.payload)
        })
        .catch(() => toastError(notifications.upgradeSubscription))
    })
  },
  fetchUpgradeCoupon: (codename, plans) =>
    dispatch(validateUpgradeCoupon(codename, plans)()).then(([_, res]) => {
      if (res.type === 'VALIDATE_UPGRADE_COUPON_SUCCESS') return res.payload
      throw new Error('Invalid coupon')
    }),
  ...modalDispatchers(dispatch)
})

export const learningPathsDispatchers = (dispatch) => ({
  loadUserLearningPaths() {
    dispatch(getUserLearningPaths())
  }
})

export const walkthroughDispatchers = (dispatch) => ({
  updateWatchedWalkthrough() {
    dispatch(updateUserProfile({ watchedWalkthroughVideo: true }))
  }
})

export const externalNewsletterDispatchers = (dispatch) => ({
  updateDeclinedExternalNewsletter() {
    dispatch(updateUserProfile({ declinedNewsletter: true }))
  },
  closeDialog() {
    dispatch(closeExternalUserFunnelDialog())
  },
  onSubmit: (data) => {
    dispatch(updateUserProfile({ alternativeEmail: data?.email }))
      .then(([_, res]) => {
        if (res.type === userTypes.USERS_PROFILE_UPDATE_SUCCESS) {
          dispatch(closeExternalUserFunnelDialog())
          toastSuccess(notifications.externalFunnel)
        } else toastFailure(notifications.externalFunnel, res.payload)
      })
      .catch((payload) => {
        const error = payload.errors
        toastServerError(error)
      })
  }
})
